import { call, put, race, take, takeLatest, takeLeading } from "redux-saga/effects"
import * as api from "./api"

// Events Redux States
import {
  GET_EVENTS,
  GET_EVENT_DETAILS,
  GET_PENALTIES,
  GET_PENALTIES_ERROR,
  GET_EVENTS_LOCATIONS,
  REFRESH_EVENTS,
  REFRESH_EVENT_ANNOUNCEMENTS,
} from "./actionTypes"
import {
  apiError,
  eventAnnouncementsRefreshed,
  eventsRefreshed,
  getEventsSuccess,
  getEventDetailsSuccess,
  getPenaltiesSuccess,
  getEventsLocationsSuccess,
} from "./actions"
import {
  EVENT_DETAILS_READ_MODE_ANNOUNCEMENTS,
  EVENT_DETAILS_READ_MODE_COMPETITION,
  EVENT_DETAILS_READ_MODE_SHELL,
} from "./eventDetailsReadPolicy"

function* getEventsLocations(filters) {
  try {
    const { data } = yield call(
      api.getEventsLocationsApi,
      filters?.payload?.filters,
      filters?.payload?.time
    )
    yield put(getEventsLocationsSuccess(data))
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getEvents({ filters, options, time, payload: [] }) {
  try {
    const { data } = yield call(api.getEvents, filters, options, time)
    yield put(getEventsSuccess(data))
  } catch (error) {
    yield put({ type: "EVENT_DIRECTORY_ERROR", payload: "Could not load events. Please try again." })
  }
}

// The list read again in the background while its texts translate (ADR 0013), one read at a time: a read that fails keeps the list, and
// the next one tries again; one that a newer read of the list overtakes is dropped.
function* refreshEvents({ filters, options, time }) {
  try {
    const { read } = yield race({ read: call(api.refreshEvents, filters, options, time), overtaken: take(GET_EVENTS) })
    if (read) yield put(eventsRefreshed(read.data))
  } catch (error) {
    // The list stays as it is.
  }
}

// An event's announcements read again in the background while one's text translates, past their cache, one read at a time: a read that
// fails, or answers with no announcements, keeps them; one that a newer read of the event overtakes is dropped.
function* refreshEventAnnouncements({ id }) {
  try {
    const { read } = yield race({ read: call(api.readEventAnnouncements, id), overtaken: take(GET_EVENT_DETAILS) })
    if (Array.isArray(read?.data?.announcements)) yield put(eventAnnouncementsRefreshed(id, read.data.announcements))
  } catch (error) {
    // The announcements stay as they are.
  }
}

const adaptCompetitionRead = data => {
  const { eventContext, competition } = data || {}

  if (!eventContext && !competition) {
    return data
  }

  return {
    ...(eventContext || {}),
    competitions: competition ? [competition] : [],
  }
}

const isUsableEventDetailsResponse = response =>
  Boolean(response?.data?._id) && Array.isArray(response?.data?.competitions)

const isUsableAnnouncementsResponse = response =>
  isUsableEventDetailsResponse(response) &&
  Array.isArray(response?.data?.announcements)

export function* getEventDetails({ payload: { id, readMode, competitionId } }) {
  try {
    let response
    if (id !== "empty") {
      if (readMode === EVENT_DETAILS_READ_MODE_ANNOUNCEMENTS) {
        try {
          response = yield call(api.getEventAnnouncements, id)
        } catch (error) {
          response = undefined
        }
        if (!isUsableAnnouncementsResponse(response)) {
          response = yield call(api.getEventDetails, id)
        }
        if (!isUsableAnnouncementsResponse(response)) {
          throw new Error("EVENT_DETAILS_UNAVAILABLE")
        }
      } else if (readMode === EVENT_DETAILS_READ_MODE_SHELL) {
        try {
          response = yield call(api.getEventShell, id)
        } catch (error) {
          response = undefined
        }
        if (!isUsableEventDetailsResponse(response)) {
          response = yield call(api.getEventDetails, id)
        }
        if (!isUsableEventDetailsResponse(response)) {
          throw new Error("EVENT_DETAILS_UNAVAILABLE")
        }
      } else if (
        readMode === EVENT_DETAILS_READ_MODE_COMPETITION &&
        competitionId
      ) {
        response = yield call(api.getEventCompetition, id, competitionId)
        if (response?.data?.eventContext?._id !== id || response?.data?.competition?._id !== competitionId) {
          throw new Error("COMPETITION_DETAILS_UNAVAILABLE")
        }
      } else {
        response = yield call(api.getEventDetails, id)
      }
    }
    yield put(
      getEventDetailsSuccess(
        id === "empty"
          ? []
          : readMode === EVENT_DETAILS_READ_MODE_COMPETITION
          ? adaptCompetitionRead(response?.data)
          : response?.data
      )
    )
  } catch (error) {
    yield put({ ...apiError(error), ...(readMode === EVENT_DETAILS_READ_MODE_COMPETITION && competitionId
      ? { meta: { competitionRead: { eventId: id, competitionId } } } : {}) })
  }
}

function* getPenalties({ payload }) {
  try {
    const { data } = yield call(api.getPenalties, { payload })
    yield put(getPenaltiesSuccess(data))
  } catch (error) {
    yield put({ type: GET_PENALTIES_ERROR })
  }
}

function* eventsSaga() {
  yield takeLatest(GET_PENALTIES, getPenalties)
  yield takeLatest(GET_EVENTS_LOCATIONS, getEventsLocations)
  yield takeLatest(GET_EVENTS, getEvents)
  yield takeLeading(REFRESH_EVENTS, refreshEvents)
  yield takeLeading(REFRESH_EVENT_ANNOUNCEMENTS, refreshEventAnnouncements)
  yield takeLatest(GET_EVENT_DETAILS, getEventDetails)
}

export default eventsSaga
